/**
 * Zustand store for auto-updates UI state.
 *
 * This store manages the client-side state for the auto-updates feature,
 * syncing with the authoritative state from the main process via IPC.
 */

import { create } from 'zustand';
import type {
  AutoUpdateState,
  AutoUpdateEvent,
} from '../types/autoUpdates';
import { createInitialAutoUpdateState } from '../types/autoUpdates';

interface AutoUpdatesStore {
  /** Current update state (mirrors main process state) */
  state: AutoUpdateState;
  /** Whether we're loading the initial state */
  isLoading: boolean;
  /** Actions */
  setState: (state: AutoUpdateState) => void;
  setLoading: (loading: boolean) => void;
  handleEvent: (event: AutoUpdateEvent) => void;
}

export const useAutoUpdatesStore = create<AutoUpdatesStore>((set) => ({
  state: createInitialAutoUpdateState(),
  isLoading: true,

  setState: (state) => set({ state, isLoading: false }),
  setLoading: (isLoading) => set({ isLoading }),

  handleEvent: (event) =>
    set((store) => {
      const { state } = store;
      switch (event.type) {
        case 'checking':
          return {
            state: { ...state, status: 'checking', errorMessage: null },
          };
        case 'update-available':
          return {
            state: {
              ...state,
              status: 'available',
              updateInfo: event.info,
              lastCheckAt: new Date().toISOString(),
              errorMessage: null,
            },
          };
        case 'update-not-available':
          return {
            state: {
              ...state,
              status: 'up-to-date',
              lastCheckAt: new Date().toISOString(),
              errorMessage: null,
              updateFallbackActive: false,
            },
          };
        case 'download-progress':
          return {
            state: {
              ...state,
              status: 'downloading',
              downloadProgress: event.progress,
              errorMessage: null,
            },
          };
        case 'update-downloaded':
          return {
            state: {
              ...state,
              status: 'downloaded',
              updateInfo: event.info,
              downloadProgress: null,
              lastCheckAt: new Date().toISOString(),
              errorMessage: null,
              updateFallbackActive: false,
            },
          };
        case 'error':
          return {
            state: {
              ...state,
              status: 'error',
              errorMessage: event.message,
              lastCheckAt: new Date().toISOString(),
            },
          };
        case 'update-fallback':
          return {
            state: {
              ...state,
              updateFallbackActive: event.active,
              // Prefer the info carried by the event; keep prior info otherwise.
              updateInfo: event.info ?? state.updateInfo,
            },
          };
        default:
          return store;
      }
    }),
}));
